/**
 * @author: Pawan Singh Sisodiya
 */
import React, { useState, useEffect, useRef } from "react";
import { Form, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import FeeReceipt from "./FeeReceipt.jsx";
import "../../App.css";
const parseSafe = (val) => {
  if (typeof val === "string") return parseInt(val.replace(/,/g, "")) || 0;
  return parseInt(val) || 0;
};

const InstallmentTable = ({
  data,
  edit,
  feeDepositHandler,
  readonly,
  feeDeposit, // Adding these back to support the shared calculation logic
  studentRecord,
}) => {
  const [installments, setInstallments] = useState([]);
  const [headsArray, setHeadsArray] = useState([]);
  const [indexValues, setIndexValues] = useState([]);
  const [flag, setFlag] = useState(0);
  const [latejoineediscount, setLateJoineeDiscount] = useState(0);

  const [selectedReceiptData, setSelectedReceiptData] = useState(null);
  const [isReceiptReady, setIsReceiptReady] = useState(false);
  const receiptRef = useRef();
  edit = edit === undefined ? false : edit;
  useEffect(() => {
    if (data) {
      const monthOrder = {
        "April to June": 1,
        "July to September": 2,
        "October to December": 3,
        "January to March": 4,
        "April": 1, "May": 2, "June": 3,
        "July": 4, "August": 5, "September": 6,
        "October": 7, "November": 8, "December": 9,
        "January": 10, "February": 11, "March": 12
      };

      const sortedData = [...data].sort((a, b) => {
        const orderA = a.orderno !== undefined && a.orderno !== null ? a.orderno : Infinity;
        const orderB = b.orderno !== undefined && b.orderno !== null ? b.orderno : Infinity;

        if (orderA !== orderB && orderA !== Infinity && orderB !== Infinity) {
          return orderA - orderB;
        }

        // Use month mapping as second priority
        const monthValA = monthOrder[a.month?.trim()] || 99;
        const monthValB = monthOrder[b.month?.trim()] || 99;

        if (monthValA !== monthValB) {
          return monthValA - monthValB;
        }

        // Fallback to due_date sorting
        const dateA = a.due_date ? new Date(a.due_date) : new Date(0);
        const dateB = b.due_date ? new Date(b.due_date) : new Date(0);
        return dateA - dateB;
      });
      setInstallments(sortedData);
    }
  }, [data]);

  useEffect(() => {
    const headsSet = new Set();
    setLateJoineeDiscount(0);





    // Source of truth for manual parts: The total saved discount in the deposit record
    const totalDepositDiscount = parseSafe(feeDeposit?.discount);
    const installmentsToProcess = installments.filter(
      (item) => item.status === "completed" || item.isCheked
    );

    // If Others/Manual is active, we just divide the user's total equally.
    // Otherwise, we use individual installment presets.
    const isManualOverall = feeDeposit?.discount !== undefined;

    const manualSharePerItem = (isManualOverall && installmentsToProcess.length > 0)
      ? (totalDepositDiscount / installmentsToProcess.length)
      : 0;

    installments.forEach((item, index) => {
      let netPayableForThisItem = 0;
      let grossPayableForThisItem = 0;

      item?.heads?.forEach((head) => {
        headsSet.add(head.headname);
      });

      // Recalculate display discount: 
      // 1. If it's already completed, use the DB value (installment_discount)
      // 2. If it's newly checked and manual is active, use the shared portion
      // 3. Otherwise use the default installment_discount (preset)

      let displayDiscount = parseSafe(item.installment_discount);

      if (item.isCheked && isManualOverall) {
        displayDiscount = manualSharePerItem;
      } else if (item.status === "completed" && edit) {
        // Keep the DB value for completed items in edit mode
        displayDiscount = parseSafe(item.installment_discount);
      }

      grossPayableForThisItem =
        parseSafe(item.installment_amount) +
        parseSafe(item.transport_fee ?? 0) -
        parseSafe(displayDiscount);

      // Use the exact previous_due coming from the backend response
      const displayPreviousDue = parseSafe(item.previous_due ?? 0);

      const totalLateFeeLiability = parseSafe(item.pushed_late_fee ?? 0) > 0 
        ? parseSafe(item.pushed_late_fee ?? 0) 
        : parseSafe(item.late_fee ?? 0);
      const remainingLateFee = Math.max(0, totalLateFeeLiability - parseSafe(item.late_fee_paid ?? 0));

      netPayableForThisItem =
        grossPayableForThisItem +
        displayPreviousDue +
        remainingLateFee -
        parseSafe(item.deposited_amount ?? 0);

      item.net_payable_amount = netPayableForThisItem;
      item.discounted_amount = displayDiscount;
      item.display_previous_due = displayPreviousDue;
      item.grossPayableAmount = grossPayableForThisItem;

      if (item.late_joinee) {
        setLateJoineeDiscount(
          (prev) => prev + item.installment_amount + item.transport_fee
        );
      }

      if (edit && item.status === "completed") {
        if (item?.edit) {
          item.isdissable = false;
          setIndexValues((previndex) => {
            return [...previndex, { index: index }];
          });
        } else {
          item.isdissable = item.isdissable === undefined ? true : item.isdissable;
        }
      } else {
        if (!edit && index === 0 && item.status !== "completed") {
          item.isdissable = false;
        } else if (!edit && index > 0 &&
          (installments[index - 1].status === "completed" || installments[index - 1].isCheked) &&
          installments[index].status === "pending"
        ) {
          item.isdissable = false;
        } else if (item.promotedstatus !== null && item.promotedstatus !== "active") {
          item.isdissable = true;
        } else {
          item.isdissable = item.isdissable === undefined ? true : item.isdissable;
        }
      }
    });

    let dissable = installments?.filter(
      (vl) => vl?.status !== "completed" && vl?.late_joinee === false
    )[0];

    if (dissable && !edit) {
      dissable.isdissable = false;
    }

    // Allow re-selection of partially paid installments
    installments.forEach(item => {
      if (item.status === 'partial' || (item.status === 'pending' && parseSafe(item.deposited_amount) > 0)) {
        item.isdissable = false;
      }
    });
    console.log("dissable", dissable);

    headsSet.add("Transport Fee");
    headsSet.add("Total Amount");
    headsSet.add("Discount");
    headsSet.add("Late Fee (Charged)");
    headsSet.add("Late Fee (Paid)");
    headsSet.add("Previous Due");
    headsSet.add("Net Payable Amount");
    headsSet.add("Deposited Amount");
    headsSet.add("Receipt Number");

    setHeadsArray(Array.from(headsSet));
  }, [installments]);

  useEffect(() => {
    const selectedInstallments = [];
    if (indexValues.length) {
      if (flag <= 0) {
        selectedInstallments.push(
          installments.filter((res, index) =>
            indexValues.some((i) => i.index === index)
          )
        );
      }

      if (selectedInstallments.length) {
        feeDepositHandler(selectedInstallments[0]);
      }
    }
  }, [indexValues]);

  const getFee = (item, headName, headFee) => {
    switch (headName) {
      case "Transport Fee":
        return formatNumberWithCommas(item.transport_fee);
      case "Previous Due":
        return formatNumberWithCommas(item?.display_previous_due) ?? 0;
      case "Deposited Amount":
        return formatNumberWithCommas(
          parseSafe(item?.deposited_amount || 0) + parseSafe(item?.late_fee_paid || 0)
        ) ?? 0;
      case "Net Payable Amount":
        return formatNumberWithCommas(item?.net_payable_amount) ?? 0;
      case "Discount":
        return formatNumberWithCommas(item?.discounted_amount) ?? 0;
      case "Total Amount":
        return formatNumberWithCommas(
          item.installment_amount + item.transport_fee
        );
      case "Late Fee (Charged)":
        const charged = parseSafe(item.pushed_late_fee ?? 0) > 0 
          ? parseSafe(item.pushed_late_fee ?? 0) 
          : parseSafe(item.late_fee ?? 0);
        return charged ? formatNumberWithCommas(charged) : 0;
      case "Late Fee (Paid)":
        return formatNumberWithCommas(item.late_fee_paid ?? 0);
      case "Receipt Number":
        if (!item.receipt_number) return "NA";
        
        const receiptNumbers = item.receipt_number.toString().split(',').map(s => s.trim());
        const depositIds = Array.isArray(item.deposit_id) 
          ? item.deposit_id 
          : (item.deposit_id?.toString() || "").split(',').map(s => s.trim());

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
            {receiptNumbers.map((num, idx) => {
              const specificId = depositIds[idx] || depositIds[0]; // Fallback to first if mismatch
              const row = { ...item, deposit_id: specificId };
              return (
                <Link 
                  key={idx} 
                  to="#" 
                  style={{ fontSize: '11px', textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrintClick(row);
                  }}
                >
                  {num}
                </Link>
              );
            })}
          </div>
        );
      default:
        return headFee;
    }
  };

  const handleChange = (e, month, index) => {
    const { checked } = e.target;
    setInstallments((prevInstallments) => {
      const updatedInstallments = [...prevInstallments];
      const selectedInstallments = [];
      updatedInstallments[index].isCheked = checked;

      if (edit) {
        if (checked) {
          if (updatedInstallments[index + 1]?.edit) {
            updatedInstallments[index + 1].isdissable = false;
          }
          updatedInstallments[index].status = "completed";
        } else {
          updatedInstallments[index].status = "pending";
          for (var i = index + 1; i < updatedInstallments.length; i++) {
            updatedInstallments[i].status = "pending";
            updatedInstallments[i].isCheked = checked;
            updatedInstallments[i].isdissable = true;
          }
        }

        selectedInstallments.push(
          updatedInstallments.filter(
            (res) =>
              res.edit && res.isdissable === false && res.status === "completed"
          )
        );
        setFlag(flag + 1);
      } else {
        if (checked) {
          if (index + 1 < updatedInstallments.length) {
            updatedInstallments[index + 1].isdissable = false;
          }
        } else {
          for (var i = index + 1; i < updatedInstallments.length; i++) {
            updatedInstallments[i].isCheked = checked;
            updatedInstallments[i].isdissable = true;
          }
        }
        selectedInstallments.push(
          updatedInstallments.filter((res) => res.isCheked === true)
        );
      }

      let dissable = installments?.filter((vl) => vl?.late_joinee === false)[0];
      if (
        selectedInstallments[0].length > 0 &&
        dissable?.id === selectedInstallments[0][0]?.id
      ) {
        selectedInstallments[0][0].late_joinee_discount = latejoineediscount;
      }
      feeDepositHandler(selectedInstallments[0]);
      return updatedInstallments;
    });
  };

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    documentTitle: "FeeReceipt",
    onAfterPrint: () => {
      setIsReceiptReady(false);
      setSelectedReceiptData(null);
    },
  });

  const handlePrintClick = (data) => {
    setIsReceiptReady(false);
    setSelectedReceiptData(data);
  };

  useEffect(() => {
    if (selectedReceiptData && isReceiptReady) {
      handlePrint();
    }
  }, [selectedReceiptData, isReceiptReady, handlePrint]);

  const formatNumberWithCommas = (number) => {
    return number ? number.toLocaleString("en-IN") : "0";
  };



  return (
    <div style={{ overflowY: "auto" }}>
      <Table striped bordered hover id="myTable">
        <thead style={{ marginLeft: "100px", width: "90px" }}>
          <tr>
            <th
              style={{
                whiteSpace: "nowrap",
                backgroundColor: "#637384",
                color: "white",
                textAlign: "center",
              }}
            >
              Heads
            </th>
            {installments.map((item, index) => (
              <th
                style={{
                  whiteSpace: "nowrap",
                  backgroundColor: "#637384",
                  color: "white",
                  textAlign: "center",
                }}
                key={item.id}
              >
                {item.month}
                <Form>
                  <Form.Check
                    inline
                    type="checkbox"
                    name={"checkedMonth"}
                    checked={
                      item?.status === "completed" ||
                      item?.isCheked
                    }
                    onChange={(e) => handleChange(e, item.month, index)}
                    disabled={
                      readonly ||
                      (!item.edit &&
                        item?.isdissable &&
                        item?.status === "completed") ||
                      item?.isdissable ||
                      item?.late_joinee
                    }
                    className={item.late_joinee ? "late-joinee" : ""}
                  />
                </Form>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ width: "140px" }}>
          {console.log("headarray", headsArray)}
          {headsArray.map((headName) => (
            <tr key={headName}>
              <td>{headName}</td>
              {installments.map((item) => {
                const head = item.heads?.find((h) => h.headname === headName);
                const headFee = head ? head.head_amount : 0;
                return (
                  <td key={item.id} className="text-end">
                    {getFee(item, headName, formatNumberWithCommas(headFee))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
      {selectedReceiptData && (
        <div style={{ display: "none" }}>
          <FeeReceipt
            ref={receiptRef}
            receiptData={selectedReceiptData}
            onReady={() => setIsReceiptReady(true)}
          />
        </div>
      )}
    </div>
  );
};

export default InstallmentTable;
